import { Box, Button, Tab, Tabs, TextField } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { HashLoader } from 'react-spinners';
import { MFA_PHONE_FIELD_LABEL, MFA_PHONE_HELPER_PROFILE } from '../../constants/mfaUserCopy';
import type { MfaChannel } from '../../types/authMfa';
import {
  mfaEmailEnrollStartApiCall,
  mfaEmailEnrollVerifyApiCall,
  mfaSmsEnrollStartApiCall,
  mfaSmsEnrollVerifyApiCall,
  mfaTotpEnrollStartApiCall,
  mfaTotpEnrollVerifyApiCall,
} from '../../services/api/users';

const CODE_PATTERN = /^[0-9]{4,10}$/;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function findOtpauthUri(binding: Record<string, unknown>): string | null {
  const seen = new Set<unknown>();
  const walk = (v: unknown): string | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'string') {
      return v.startsWith('otpauth://') ? v : null;
    }
    if (typeof v !== 'object' || seen.has(v)) return null;
    seen.add(v);
    for (const val of Object.values(v as Record<string, unknown>)) {
      const found = walk(val);
      if (found) return found;
    }
    return null;
  };
  return walk(binding);
}

type FlowStep = 'idle' | 'pending' | 'done';

interface MethodState {
  step: FlowStep;
  enrollToken?: string;
  masked?: string;
}

export type MfaEnrollmentVariant = 'login' | 'account';

export interface MfaEnrollmentSetupProps {
  /** When verify returns a full `accessToken` (enrollment-only JWT path), complete session and leave this screen. */
  onFullSession?: (accessToken: string) => void | Promise<void>;
  onBack: () => void;
  /** Login first-time setup vs. signed-in account management (dialog). */
  variant?: MfaEnrollmentVariant;
  /** Show only one method (e.g. add/change SMS). */
  channelOnly?: MfaChannel;
  /** After successful verify with a normal session (no new accessToken in body). */
  onMethodAdded?: () => void;
}

const MfaEnrollmentSetup: React.FC<MfaEnrollmentSetupProps> = ({
  onFullSession,
  onBack,
  variant = 'login',
  channelOnly,
  onMethodAdded,
}) => {
  const [tab, setTab] = useState(0);
  const [sms, setSms] = useState<MethodState>({ step: 'idle' });
  const [email, setEmail] = useState<MethodState>({ step: 'idle' });
  const [totp, setTotp] = useState<MethodState & { binding?: Record<string, unknown> }>({ step: 'idle' });
  const [phoneOverride, setPhoneOverride] = useState('');
  const [emailOverride, setEmailOverride] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anyDone, setAnyDone] = useState(false);

  useEffect(() => {
    if (channelOnly === 'sms') setTab(0);
    else if (channelOnly === 'email') setTab(1);
    else if (channelOnly === 'totp') setTab(2);
  }, [channelOnly]);

  const clearError = useCallback(() => setError(null), []);

  const showSms = channelOnly ? channelOnly === 'sms' : tab === 0;
  const showEmail = channelOnly ? channelOnly === 'email' : tab === 1;
  const showTotp = channelOnly ? channelOnly === 'totp' : tab === 2;

  const handleSmsStart = useCallback(async () => {
    clearError();
    const phone = phoneOverride.trim();
    if (!phone) {
      setError('Enter your mobile number.');
      return;
    }
    setLoading(true);
    try {
      const res = await mfaSmsEnrollStartApiCall({ phone });
      setSms({
        step: 'pending',
        enrollToken: res.data.enrollToken,
        masked: res.data.phoneMasked,
      });
      setSmsCode('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Could not start SMS enrollment.');
    } finally {
      setLoading(false);
    }
  }, [clearError, phoneOverride]);

  const handleSmsVerify = useCallback(async () => {
    if (!sms.enrollToken || !CODE_PATTERN.test(smsCode.trim())) return;
    clearError();
    setLoading(true);
    try {
      const res = await mfaSmsEnrollVerifyApiCall({ enrollToken: sms.enrollToken, code: smsCode.trim() });
      if (res.data.accessToken) {
        if (variant === 'login' && onFullSession) {
          await onFullSession(res.data.accessToken);
        }
        return;
      }
      if (variant === 'account') {
        onMethodAdded?.();
        return;
      }
      setSms(s => ({ ...s, step: 'done' }));
      setAnyDone(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Invalid code. Try again.');
    } finally {
      setLoading(false);
    }
  }, [clearError, onFullSession, onMethodAdded, sms.enrollToken, smsCode, variant]);

  const handleEmailStart = useCallback(async () => {
    clearError();
    const email = emailOverride.trim();
    if (!email) {
      setError('Enter your email address.');
      return;
    }
    if (!EMAIL_PATTERN.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const res = await mfaEmailEnrollStartApiCall({ email });
      setEmail({
        step: 'pending',
        enrollToken: res.data.enrollToken,
        masked: res.data.emailMasked,
      });
      setEmailCode('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Could not start email enrollment.');
    } finally {
      setLoading(false);
    }
  }, [clearError, emailOverride]);

  const handleEmailVerify = useCallback(async () => {
    if (!email.enrollToken || !CODE_PATTERN.test(emailCode.trim())) return;
    clearError();
    setLoading(true);
    try {
      const res = await mfaEmailEnrollVerifyApiCall({ enrollToken: email.enrollToken, code: emailCode.trim() });
      if (res.data.accessToken) {
        if (variant === 'login' && onFullSession) {
          await onFullSession(res.data.accessToken);
        }
        return;
      }
      if (variant === 'account') {
        onMethodAdded?.();
        return;
      }
      setEmail(s => ({ ...s, step: 'done' }));
      setAnyDone(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Invalid code. Try again.');
    } finally {
      setLoading(false);
    }
  }, [clearError, email.enrollToken, emailCode, onFullSession, onMethodAdded, variant]);

  const handleTotpStart = useCallback(async () => {
    clearError();
    setLoading(true);
    try {
      const res = await mfaTotpEnrollStartApiCall({});
      setTotp({
        step: 'pending',
        enrollToken: res.data.enrollToken,
        binding: res.data.binding as Record<string, unknown>,
      });
      setTotpCode('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Could not start authenticator enrollment.');
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  const handleTotpVerify = useCallback(async () => {
    if (!totp.enrollToken || !CODE_PATTERN.test(totpCode.trim())) return;
    clearError();
    setLoading(true);
    try {
      const res = await mfaTotpEnrollVerifyApiCall({ enrollToken: totp.enrollToken, code: totpCode.trim() });
      if (res.data.accessToken) {
        if (variant === 'login' && onFullSession) {
          await onFullSession(res.data.accessToken);
        }
        return;
      }
      if (variant === 'account') {
        onMethodAdded?.();
        return;
      }
      setTotp(s => ({ ...s, step: 'done' }));
      setAnyDone(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Invalid code. Try again.');
    } finally {
      setLoading(false);
    }
  }, [clearError, onFullSession, onMethodAdded, totp.enrollToken, totpCode, variant]);

  const otpauthUri = totp.binding ? findOtpauthUri(totp.binding) : null;

  const isLogin = variant === 'login';

  return (
    <Box sx={{ mt: isLogin ? 2 : 0 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, textAlign: 'center', margin: '0 0 8px' }}>
        {isLogin ? 'Set up two-step verification' : 'Verification method'}
      </h2>
      <p style={{ fontSize: 14, color: 'rgba(0,0,0,0.75)', textAlign: 'center', marginBottom: 16 }}>
        {isLogin
          ? 'Your account requires at least one verification method. After you enter a valid code below, you will be signed in automatically.'
          : 'We will send a code for SMS or email, or show a QR code for your authenticator app. For SMS or email, enter the number or address where you want to receive codes (SMS must include country code).'}
      </p>
      {error && (
        <p style={{ color: '#c62828', fontSize: 14, textAlign: 'center', marginBottom: 8 }} role="alert">
          {error}
        </p>
      )}
      {anyDone && isLogin && (
        <p style={{ fontSize: 14, color: '#2e7d32', textAlign: 'center', marginBottom: 12 }}>
          Method saved. You can add another from account settings when signed in, or return to sign in.
        </p>
      )}
      {!channelOnly && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ mb: 2 }}>
          <Tab label="SMS" disabled={loading} />
          <Tab label="Email" disabled={loading} />
          <Tab label="App" disabled={loading} />
        </Tabs>
      )}

      {showSms && (
        <Box>
          {sms.step === 'done' && isLogin ? (
            <p style={{ fontSize: 14 }}>SMS is enrolled.</p>
          ) : (
            <>
              <TextField
                size="small"
                fullWidth
                required
                label={MFA_PHONE_FIELD_LABEL}
                value={phoneOverride}
                onChange={e => setPhoneOverride(e.target.value)}
                disabled={loading || sms.step === 'pending'}
                className="login-fields"
                helperText={MFA_PHONE_HELPER_PROFILE}
              />
              {sms.step !== 'pending' && (
                <Button
                  className="full-width mt-2"
                  variant="outlined"
                  onClick={handleSmsStart}
                  disabled={loading || !phoneOverride.trim()}
                >
                  {loading ? <HashLoader size={24} color="#1b6bae" /> : 'Send verification code'}
                </Button>
              )}
              {sms.step === 'pending' && sms.masked && (
                <>
                  <p style={{ fontSize: 14, marginTop: 12 }}>Code sent to {sms.masked}</p>
                  <TextField
                    size="small"
                    fullWidth
                    label="Verification code"
                    value={smsCode}
                    onChange={e => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    inputProps={{ inputMode: 'numeric' }}
                    className="login-fields"
                  />
                  <Button
                    className="full-width mt-2"
                    variant="contained"
                    onClick={handleSmsVerify}
                    disabled={loading || !CODE_PATTERN.test(smsCode.trim())}
                  >
                    {loading ? <HashLoader size={24} color="#fff" /> : 'Verify'}
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
      )}

      {showEmail && (
        <Box>
          {email.step === 'done' && isLogin ? (
            <p style={{ fontSize: 14 }}>Email is enrolled.</p>
          ) : (
            <>
              <TextField
                size="small"
                fullWidth
                required
                label="Email"
                type="email"
                value={emailOverride}
                onChange={e => setEmailOverride(e.target.value)}
                disabled={loading || email.step === 'pending'}
                className="login-fields"
                helperText="Enter the address where you want to receive verification codes."
              />
              {email.step !== 'pending' && (
                <Button
                  className="full-width mt-2"
                  variant="outlined"
                  onClick={handleEmailStart}
                  disabled={loading || !emailOverride.trim()}
                >
                  {loading ? <HashLoader size={24} color="#1b6bae" /> : 'Send verification code'}
                </Button>
              )}
              {email.step === 'pending' && email.masked && (
                <>
                  <p style={{ fontSize: 14, marginTop: 12 }}>Code sent to {email.masked}</p>
                  <TextField
                    size="small"
                    fullWidth
                    label="Verification code"
                    value={emailCode}
                    onChange={e => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    inputProps={{ inputMode: 'numeric' }}
                    className="login-fields"
                  />
                  <Button
                    className="full-width mt-2"
                    variant="contained"
                    onClick={handleEmailVerify}
                    disabled={loading || !CODE_PATTERN.test(emailCode.trim())}
                  >
                    {loading ? <HashLoader size={24} color="#fff" /> : 'Verify'}
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
      )}

      {showTotp && (
        <Box>
          {totp.step === 'done' && isLogin ? (
            <p style={{ fontSize: 14 }}>Authenticator app is enrolled.</p>
          ) : (
            <>
              {totp.step !== 'pending' && (
                <Button className="full-width" variant="outlined" onClick={handleTotpStart} disabled={loading}>
                  {loading ? <HashLoader size={24} color="#1b6bae" /> : 'Generate QR code'}
                </Button>
              )}
              {totp.step === 'pending' && totp.binding && (
                <>
                  {otpauthUri && (
                    <Box sx={{ textAlign: 'center', my: 2 }}>
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(otpauthUri)}`}
                        alt="Authenticator QR code"
                        width={180}
                        height={180}
                      />
                    </Box>
                  )}
                  {!otpauthUri && (
                    <p style={{ fontSize: 13, marginTop: 8 }}>
                      Scan or enter the secret shown in your authenticator app using the data from your administrator if
                      no QR appears.
                    </p>
                  )}
                  <TextField
                    size="small"
                    fullWidth
                    label="Code from app"
                    value={totpCode}
                    onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    inputProps={{ inputMode: 'numeric' }}
                    className="login-fields"
                  />
                  <Button
                    className="full-width mt-2"
                    variant="contained"
                    onClick={handleTotpVerify}
                    disabled={loading || !CODE_PATTERN.test(totpCode.trim())}
                  >
                    {loading ? <HashLoader size={24} color="#fff" /> : 'Verify'}
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
      )}

      {isLogin && anyDone && (
        <Button size="medium" variant="contained" color="success" className="full-width mt-3" onClick={onBack} disabled={loading}>
          Return to sign in
        </Button>
      )}
      {isLogin ? (
        <Button size="small" variant="text" className="full-width mt-1" onClick={onBack} disabled={loading}>
          Back to sign in
        </Button>
      ) : (
        <Button size="small" variant="outlined" className="full-width mt-3" onClick={onBack} disabled={loading}>
          Close
        </Button>
      )}
    </Box>
  );
};

export default MfaEnrollmentSetup;
